import React, { useState, useEffect, useContext } from "react";
import { LangContext } from "../../contexts/LangContext";
import { apiService } from "../../services/api";
import { setCookie } from "../../services/cookie";
import { useToasts } from "../../hooks/useToasts";
import { ToastCenter } from "../Common/ToastCenter";
import { LogoSVG } from "../Common/LogoSVG";
import { LangSwitchInline } from "../Common/LangSwitch";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { getTrackingParams, captureTrackingParams } from "../../utils/tracking";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

const GOOGLE_CLIENT_ID = "266392031314-0dbutji2he949k88g3otfpj448ci3dth.apps.googleusercontent.com";
const REDIRECT_URI = "https://flautreach.com/googleauth";

export function AuthPage({ onLogin }) {
  const { lang } = useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const { toasts, add, remove } = useToasts();

  useEffect(() => {
    captureTrackingParams(lang);
  }, [lang]);

  useEffect(() => {
    const handleGoogleRedirect = async () => {
      const hash = window.location.hash;
      
      if (hash && hash.includes('id_token')) {
        setGoogleLoading(true);
        
        try {
          const params = new URLSearchParams(hash.substring(1));
          const idToken = params.get('id_token');
          
          if (!idToken) {
            throw new Error('No id_token received from Google');
          }

          const tracking = getTrackingParams();
          
          const response = await apiService.googleAuth(idToken, {
            ...tracking,
            locale: tracking.locale || lang,
          });
          
          setCookie("authToken", response.access, 7);
          setCookie("userEmail", response.user?.email || '', 7);
          
          add(t.googleAuthSuccess || "Google login successful", "success");
          onLogin(response.user || { email: response.user?.email });
          
          window.history.replaceState(null, '', window.location.pathname);
          
        } catch (err) {
          if (err.code === 'email_not_verified') {
            add(t.verifyEmailToLogin || "Please verify your email before logging in.", "error");
            setShowResend(true);
          } else {
            add(err.message || "Google authentication failed", "error");
          }
          window.history.replaceState(null, '', window.location.pathname);
        } finally {
          setGoogleLoading(false);
        }
      }
    };

    handleGoogleRedirect();
  }, []);

  const handleLogin = async (email, pass) => {
    try {
      const res = await apiService.login(email, pass);
      setCookie("authToken", res.access, 7);
      setCookie("userEmail", email, 7);
      onLogin(res.user || { email });
    } catch (err) {
      if (err.code === 'email_not_verified') {
        add(t.verifyEmailToLogin || "Please verify your email before logging in. Check your inbox.", "warning");
        setUnverifiedEmail(email);
        setShowResend(true);
      } else {
        add(err.message, "error");
      }
    }
  };

  const handleRegister = async (email, pass, pass2) => {
    if (pass !== pass2) throw new Error(t.passwordsDontMatch);
    if (pass.length < 8) throw new Error(t.passwordTooShort);
    
    const tracking = getTrackingParams();
    
    await apiService.register(email, pass, pass2, {
      ...tracking,
      locale: tracking.locale || lang,
    });
    
    add(t.accountCreated, "success");
    setTab("login");
    setPass("");
    setPass2("");
  };

  const handleForgotPassword = async (email) => {
    await apiService.forgotPassword(email);
    add(t.resetSent, "success");
    setForgot(false);
  };

  const handleResendVerification = async () => {
    const emailToUse = unverifiedEmail || email;
    
    if (!emailToUse) {
      add(t.enterEmailFirst || "Please enter your email first", "error");
      return;
    }

    setResendLoading(true);
    try {
      await apiService.resendVerification(emailToUse);
      add(t.verifySent || "Verification email sent! Check your inbox.", "success");
    } catch (err) {
      add(err.message || "Failed to resend verification", "error");
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=id_token+token&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=openid+email+profile&nonce=${Math.random().toString(36).substring(2)}`;
    window.location.href = googleAuthUrl;
  };

  return (
    <>
      <ToastCenter toasts={toasts} onRemove={remove} />
      <div className="auth-page">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="auth-brand-icon">
              <LogoSVG size={26} />
            </div>
            <h1>{t.appName}</h1>
            <p>{t.appTagline}</p>
          </div>
          <div className="auth-card">
            <LangSwitchInline />
            {!forgot ? (
              <>
                <div className="auth-tabs">
                  <button
                    className={`auth-tab ${tab === "login" ? "active" : ""}`}
                    onClick={() => { setTab("login"); setShowResend(false); }}
                  >
                    {t.signIn}
                  </button>
                  <button
                    className={`auth-tab ${tab === "reg" ? "active" : ""}`}
                    onClick={() => { setTab("reg"); setShowResend(false); }}
                  >
                    {t.signUp}
                  </button>
                </div>
                
                {showResend ? (
                  <div style={{
                    background: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: 10,
                    padding: "16px",
                    marginBottom: 20,
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📧</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#856404", marginBottom: 4 }}>
                      {t.verifyEmailToLogin || "Email not verified"}
                    </div>
                    <div style={{ fontSize: 13, color: "#856404", marginBottom: 12 }}>
                      {t.checkInbox || "Check your inbox and click the verification link."}
                    </div>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                      style={{
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "none",
                        background: "#ffc107",
                        color: "#856404",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {resendLoading ? "…" : t.resendVerificationBtn || "Resend verification email"}
                    </button>
                    <button
                      onClick={() => setShowResend(false)}
                      style={{
                        display: "block",
                        margin: "8px auto 0",
                        padding: "6px 12px",
                        borderRadius: 6,
                        border: "none",
                        background: "transparent",
                        color: "#856404",
                        fontSize: 12,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {t.backToLogin || "Back to login"}
                    </button>
                  </div>
                ) : (
                  <>
                    {tab === "login" ? (
                      <LoginForm
                        t={t}
                        email={email}
                        setEmail={setEmail}
                        pass={pass}
                        setPass={setPass}
                        loading={loading || googleLoading}
                        setLoading={setLoading}
                        onLogin={handleLogin}
                        onForgotPassword={() => setForgot(true)}
                        addToast={add}
                      />
                    ) : (
                      <RegisterForm
                        t={t}
                        email={email}
                        setEmail={setEmail}
                        pass={pass}
                        setPass={setPass}
                        pass2={pass2}
                        setPass2={setPass2}
                        loading={loading || googleLoading}
                        setLoading={setLoading}
                        onRegister={handleRegister}
                        addToast={add}
                      />
                    )}

                    <div style={{ marginTop: "20px" }}>
                      
                      
                      
                      <button
                        className="google-auth-button"
                        onClick={handleGoogleLogin}
                        disabled={loading || googleLoading}
                        style={{
                          width: "100%",
                          padding: "12px",
                          border: "1px solid #dadce0",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#3c4043",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#fff"}
                      >
                        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.96 16.46 0 20.12 0 24c0 3.88.96 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        {googleLoading ? "..." : `${t.continueWithGoogle || "Continue with Google"}`}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <ForgotPasswordForm
                t={t}
                email={email}
                setEmail={setEmail}
                loading={loading || googleLoading}
                setLoading={setLoading}
                onForgotPassword={handleForgotPassword}
                onCancel={() => setForgot(false)}
                addToast={add}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}